import moment from 'moment';
import { format, isFuture, isToday, isPast } from 'date-fns';
import {
  parseFromTimeZone,
  formatToTimeZone,
  convertToTimeZone,
  parseFromString,
} from 'date-fns-timezone';
import store from '@/store';

const DATE_FORMAT = 'MMM D, YYYY';
const SHORT_DATE_FORMAT = 'MM-DD-YY hh:mma';
const DATETIME_FORMAT = 'MMM D, YYYY h:mm A';
const TIME12_FORMAT = 'h:mm a';
const YEAR_MONTH_DAY_FORMAT = 'YYYY-MM-DD';
const MONTH_DAY_YEAR_FORMAT = 'MM-DD-YYYY';
const DESCRIPTIVE_FORMAT = 'MMMM Do, YYYY';
const MONTH_DAY_YEAR_EXTENDED_FORMAT = 'MM/DD/YYYY h:mm a'; 
const MONTH_DAY_YEAR_FORMAT_REGEX = /^(0[1-9]|10|[0-1][1-2])-(0[1-9]|10|[1-2][0-9]|3[0-1])-\d{4}$/;

/**
 * Validates if the date has a valid month-day relation
 * For example... the date is not February 30 or June 31
 * @return {Boolean} if month day is valid, returns true
 */
function isMonthDayValid( month, day, year) {
  var d = new Date(year, month, day);

  return d.getFullYear() == year && d.getMonth() == month && d.getDate() == day;
}

const isYearMonthDayDateFormatValid = (date = '') => {
  if (MONTH_DAY_YEAR_FORMAT_REGEX.test(date)) {
    const d = date.split('-');

    return isMonthDayValid(Number(d[0]) - 1, d[1], d[2]);
  }

  return false;
}


const formatDateTime = date => {
  return date ? format(date, DATETIME_FORMAT) : null;
};

const formatDate = date => {
  return date ? format(date, DATE_FORMAT) : null;
};

const formatDateMonthDayYear = date => {
  return date ? format(date, MONTH_DAY_YEAR_FORMAT) : null;
};

const formatDateDescriptive = date => {
  return date ? format(date, DESCRIPTIVE_FORMAT) : null;
};

const formatDateMonthDayYearExtended = date => {
  return date ? format(date, MONTH_DAY_YEAR_EXTENDED_FORMAT) : null;
};

const formatTime12 = date => {
  return date ? format(date, TIME12_FORMAT) : null;
};

/**
 * Fetches the user's set timezone
 * @return {String} the timezone if it exists, else return null
 */
const getUserTimeZone = () => {
  const { user } = store.state.userInformation;
  if (user && user.timezone) return user.timezone;
  return null;
};

/**
 * Takes the date, and parses it to local from the users timezone.
 * This date should take the format 'YYYY-MM-DD h:mm a'
 * @param  {String|Date|Number} date the date string
 * @return {String}             the local time converted datetime string
 */
const parseFromUserTimeZone = date => {
  if (!date) {
    return null;
  }
  const userTimeZone = getUserTimeZone();
  return userTimeZone
    ? parseFromTimeZone(date, `${YEAR_MONTH_DAY_FORMAT} ${TIME12_FORMAT}`, { timeZone: userTimeZone })
    : parseFromString(date, `${YEAR_MONTH_DAY_FORMAT} ${TIME12_FORMAT}`);
};

/**
 * Grabs the user's timezone, and format the date to the timezone
 * @param  {String|Date|Number} date the date string
 * @return {String}             the formatted and converted datetime string
 */
const convertToYearMonthDay = date => {
  if (!date) {
    return null;
  }
  const userTimeZone = getUserTimeZone();
  return userTimeZone ?
    formatToTimeZone(date, YEAR_MONTH_DAY_FORMAT, { timeZone: userTimeZone }) :
    formatDateTime(date);
};


/**
 * Grabs the user's timezone, and format the date to the timezone as a datetime
 * @param  {String|Date|Number} date the date string
 * @return {String}             the formatted and converted datetime string
 */
const convertDateTime = date => {
  if (!date) {
    return null;
  }
  const userTimeZone = getUserTimeZone();
  return userTimeZone ?
    formatToTimeZone(date, DATETIME_FORMAT, { timeZone: userTimeZone }) :
    formatDateTime(date);
};

const formatShortDate = date => {
  if (!date) {
    return null;
  }
  const userTimeZone = getUserTimeZone();
  return userTimeZone ?
    formatToTimeZone(date, SHORT_DATE_FORMAT, { timeZone: userTimeZone }) :
    formatDateTime(date);
};

/**
 * Grabs the user's timezone, and format the date to the timezone as a date
 * @param  {String|Date|Number} date the date string
 * @return {String}             the formatted and converted date string
 */
const convertDate = date => {
  if (!date) {
    return null;
  }
  const userTimeZone = getUserTimeZone();
  return userTimeZone ?
    formatToTimeZone(date, DATE_FORMAT, { timeZone: userTimeZone }) :
    formatDate(date);
};

/**
 * Grabs the user's timezone, and format the date to the timezone as a time
 * @param  {String|Date|Number} date the date string
 * @return {String}             the formatted and converted 12h - time string
 */
const convertTime12 = date => {
  if (!date) {
    return null;
  }
  const userTimeZone = getUserTimeZone();
  return userTimeZone ?
    formatToTimeZone(date, TIME12_FORMAT, { timeZone: userTimeZone }) :
    formatTime12(date);
};

/**
 * Convert the date to the user's timezone if one exists, else return the original
 * date. This retains the original format.
 * @param  {String|Date|Number} date the original date
 * @return {String|Date|Number}      converted date
 */
const convertToUserTimeZone = date => {
  if (!date) {
    return null;
  }
  const userTimeZone = getUserTimeZone();
  return userTimeZone ? convertToTimeZone(date, { timeZone: userTimeZone }) : date;
};

/**
 * Get the current date in the users timezone if one exists, else revert to using
 * the browser timezone
 * @return {String} the date string in 'YYYY-MM-DD'
 */
const currentDate = () => {
  const userTimeZone = getUserTimeZone();
  return userTimeZone ?
    formatToTimeZone(new Date(), YEAR_MONTH_DAY_FORMAT, { timeZone: userTimeZone }) :
    format(new Date(), YEAR_MONTH_DAY_FORMAT);
};

/**
 * Get the current date time in the users timezone if one exists, else revert to using
 * the browser timezone
 *
 * @return {Object} the date string in 'MMM D, YYYY h:mm A'
 */
const currentDateTime = () => {
  const userTimeZone = getUserTimeZone();
  const localDateTime = new Date();
  const userDateTime = convertToTimeZone(localDateTime, { timeZone: userTimeZone });
  return userTimeZone ? userDateTime : localDateTime;
};

/**
 * Function to split a time object into it's corresponding hour and minutes
 * @param {String} time is the time in 'HH:MM a' or 'HH:MMa' format
 * @returns {Object} segments is an object containing the 'hour', 'minutes'
 */
const split12Time = time => {
  const splits = time.split(':');
  if (splits.length < 2) return null;

  let hour = splits[0];
  hour = (parseInt(hour, 10) % 12);

  const minuteAP = splits[1].split(' ');
  let minutes;
  let ap;
  // Handles the case of '12:30 pm'
  if (minuteAP.length === 2) {
    [minutes, ap] = minuteAP;
  } else {
    // Handles the case of '12:30pm'
    minutes = `${minuteAP[0].charAt(0)}${minuteAP[0].charAt(1)}`;
    ap = `${minuteAP[0].charAt(minuteAP[0].length - 2)}${minuteAP[0].charAt(minuteAP[0].length - 1)}`;
  }

  if (ap === 'pm') hour += 12;

  return { hour, minutes };
};

/**
 * Helper function to convert a 12-hour time to 24
 * @param {String} time is the time in 'HH:MM a' or 'HH:MMa' format
 * @returns {String} The 24-hour-time counterpart
 */
const convert12To24 = time => {
  if (!time) return null;
  const segments = split12Time(time);
  if (segments) {
    const { hour, minutes } = segments;
    return `${hour}:${minutes}:00`;
  }
  return time;
};

/**
 * Helper function to convert a 24-hour time to 12
 * @param {String} time is the time in H:MM a a format
 * @returns {String} The 12-hour-time counterpart
 */
const convert24To12 = time => {
  const splits = time.split(':');
  if (splits.length >= 2) {
    let hour = parseInt(splits[0], 10);
    const minutes = splits[1];

    // If it is in the afternoon
    if (hour >= 12) {
      hour -= 12;
      // 12:01 pm instead of 00:01 pm
      if (hour === 0) hour = 12;
      return `${hour}:${minutes} pm`;
    }
    // 12:01 am instead of 00:01 am
    if (hour === 0) hour = 12;
    return `${hour}:${minutes} am`;
  }
  return time;
};

/**
 * Converts miliseconds from midnight to a 24-hour time format
 * More details in this stack overflow link:
 * stackoverflow.com/questions/19700283/
 * how-to-convert-time-milliseconds-to-hours-min-sec-format-in-javascript
 *
 * @param {Number} ms Is the number of miliseconds that has passed since midnight
 */
const milisecondsToTime24 = ms => {
  let minutes = parseInt((ms / (1000 * 60)) % 60, 10);
  let hours = parseInt((ms / (1000 * 60 * 60)) % 24, 10);

  hours = (hours < 10) ? `0${hours}` : hours;
  minutes = (minutes < 10) ? `0${minutes}` : minutes;

  return `${hours}:${minutes}`;
};

const milisecondsToTime12 = ms => {
  return convert24To12(milisecondsToTime24(ms));
};

const afterYesterday = (date, tz = 'PST') => {
  const desiredDate = new Date(`${date} ${tz}`);
  return isFuture(desiredDate) || isToday(desiredDate);
};

const afterToday = (date, tz = 'PST') => {
  date = date.replace(/-/g,'/');
  const desiredDate = new Date(`${date} ${tz}`);
  return isFuture(desiredDate);
};

const isBeforeToday = (date, tz = 'PST') => {
  const desiredDate = new Date(`${date.replace(/-/g,'/')} ${tz}`);
  return isPast(desiredDate) && !isToday(desiredDate);
};

const formatFromUnixToCalendar = unix => {
  return moment.unix(unix).format(MONTH_DAY_YEAR_EXTENDED_FORMAT);
};

export {
  isYearMonthDayDateFormatValid,
  convertToYearMonthDay,
  parseFromUserTimeZone,
  convertDateTime,
  convertDate,
  convertTime12,
  convertToUserTimeZone,
  formatDate,
  formatDateTime,
  formatTime12,
  currentDate,
  currentDateTime,
  split12Time,
  convert12To24,
  convert24To12,
  milisecondsToTime24,
  milisecondsToTime12,
  afterYesterday,
  afterToday,
  isBeforeToday,
  formatShortDate,
  formatDateMonthDayYear,
  formatDateMonthDayYearExtended,
  formatFromUnixToCalendar,
  formatDateDescriptive,
};
